import React, { useState, type ComponentType } from 'react';
import { lazy } from 'react-loosely-lazy';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import {
	ExperienceErrorBoundary,
	ExperienceStart,
} from '@atlassian/jira-business-experience-tracking';
import { ThemedButton } from '@atlassian/jira-business-theme-components';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	ExportTableProps,
	ExportModalProps,
} from '@atlassian/jira-software-roadmap-image-export';
import { EXPORT_IMAGE_EXPERIENCE } from '../../../common/constants';
import { LazySharingExport } from './export-share';
import messages from './messages';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncSharingModal = lazy<ComponentType<ExportModalProps>>(
	() =>
		import(
			/* webpackChunkName: "async-roadmap-share-modal" */ '@atlassian/jira-software-roadmap-image-export/src/modal'
		),
	{ ssr: false },
);

const EXPORT_ICON = <ShortcutIcon label="" size="small" />;

type Props = {
	tableProps: ExportTableProps;
};

export const ExportToImage = ({ tableProps }: Props) => {
	const [open, setOpen] = useState(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	return (
		<ExperienceErrorBoundary experience={EXPORT_IMAGE_EXPERIENCE}>
			{open && (
				<ContextualAnalyticsData sourceName="exportImageShare" sourceType={MODAL}>
					<ExperienceStart experience={EXPORT_IMAGE_EXPERIENCE} />

					<Placeholder name="sharing-modal" fallback={null}>
						<AsyncSharingModal
							ExportImage={LazySharingExport}
							tableProps={tableProps}
							onClickDownload={(_, analyticsEvent) => {
								fireUIAnalytics(analyticsEvent, 'saveImageButton');
							}}
							onClose={() => {
								fireUIAnalytics(createAnalyticsEvent({}), 'exportImageShare closed');
								setOpen(false);
							}}
						/>
					</Placeholder>
				</ContextualAnalyticsData>
			)}

			<ThemedButton
				onClick={(_, analyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'exportToImage');
					setOpen(true);
				}}
				appearance="subtle"
				iconBefore={EXPORT_ICON}
				testId="business-timeline.ui.timeline-view.export-image"
			>
				{formatMessage(messages.export)}
			</ThemedButton>
		</ExperienceErrorBoundary>
	);
};
